import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { setUser as reportUser } from '../lib/report'
import posthog from 'posthog-js'
import type { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { Profile, SubAccount, TeamMember } from '../types/database'
import type { TeamRole } from '../lib/roles'
import { applyAttributionIfPending } from '../lib/referralAttribution'
// Phase 62.E.1 — activation events instrumentation
import { emitActivationEvent } from '../lib/activationEvents'
import { signOutEverywhere, startPushTokenWatch } from '../lib/push'
import { visibleAccounts } from '@/lib/accountClosure'
import {
  activeOnLoad,
  resolveActiveSubAccount,
  type ServerChoice,
} from '../lib/activeBusinessOnLoad'

// Phase 62.D: account type from merchant_groups.
export type AccountType = 'solo' | 'multi_business' | 'agency'

interface AuthContextValue {
  user: User | null
  profile: Profile | null
  profileExists: boolean | null
  // Phase 28B: team membership context. The currentTeamMember row is
  // the team_members entry for the user's currently active sub_account.
  // For users with one sub_account this is unambiguous; Phase C-B1
  // added multi-membership support so this now reflects the active
  // selection (see availableSubAccounts + setActiveSubAccount).
  currentTeamMember: TeamMember | null
  // Phase 49: full 9-role catalog (+ transitional 'agent'). Was the legacy
  // 3-value union; widened so capability gates resolve correctly for every role.
  currentTeamRole: TeamRole | null
  currentTeamOwnerId: string | null
  // Phase 28C: full list of accepted team_members rows for the owner's
  // team. Used by Settings -> Team to render the active members list.
  // Lazy-loaded; null until first explicit fetch. Distinct from
  // availableSubAccounts (which is the user's own memberships across
  // sub_accounts, not the active sub_account's roster).
  teamMembers: TeamMember[] | null
  // Phase 28.6C: sub-account context. currentSubAccount is the
  // sub_accounts row pointed at by the active membership. For solo
  // operators (one sub_account) this is the default sub-account
  // auto-created at signup; consumers shouldn't need to think about
  // it. currentSubAccountId is the convenience accessor used by
  // queries that scope by sub_account_id.
  currentSubAccount: SubAccount | null
  currentSubAccountId: string | null
  // Phase C-B1: multi-membership context. availableSubAccounts is the
  // full set of sub_accounts the user belongs to (one entry per
  // accepted team_members row). For users with one sub_account this
  // is a singleton array; the SubAccountSwitcher renders nothing in
  // that case. setActiveSubAccount(id) flips the active selection
  // and persists to localStorage scoped by user.id.
  availableSubAccounts: SubAccount[]
  setActiveSubAccount: (subAccountId: string) => void
  // Phase 62.D: account type from merchant_groups. Defaults to 'solo'
  // when the user has no merchant_group row (agents, pre-migration
  // accounts).
  accountType: AccountType
  loading: boolean
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  refreshTeam: () => Promise<void>
  refreshTeamMembers: () => Promise<void>
  refreshSubAccount: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/**
 * PLATFORM-1 — the auth context WITHOUT the provider requirement.
 *
 * useAuth throws when there is no provider, which is right for a page: a page
 * that cannot tell who is signed in should not render. It is wrong for a
 * purely additive layer like the account menu configuration, which must degrade
 * to "nothing configured" rather than take down every component that renders a
 * sidebar in a test harness.
 */
export function useOptionalAuth(): AuthContextValue | undefined {
  return useContext(AuthContext)
}

// localStorage key for the user's last-active sub_account selection.
// Scoped by user.id so two users sharing a browser don't pollute each
// other's selection.
function activeSubAccountStorageKey(userId: string): string {
  return `service_club:active_sub_account:${userId}`
}

/**
 * SHELF-FLASH-2 — WHEN THE CHOICE WAS MADE, alongside what it was.
 *
 * A stored id used to beat a brand new account outright: create a business,
 * get sent to the dashboard, and land in the OLD one, seeing its sections. The
 * fix needs to know whether the choice predates the new account, so the write
 * records the moment. A missing or unreadable stamp reads as LONG AGO, which
 * loses to any account — the safe direction, because the cost of ignoring a
 * remembered choice is one extra click and the cost of honouring a stale one
 * is a merchant in the wrong business.
 */
function activeSubAccountStampKey(userId: string): string {
  return `service_club:active_sub_account_at:${userId}`
}

/** Millisecond stamp of when the stored choice was made, or 0 if unknown. */
export function readChoiceStamp(raw: string | null): number {
  const n = Number(raw ?? '')
  return Number.isFinite(n) && n > 0 ? n : 0
}

// THE-ACTIVE-BUSINESS-IS-NOT-FLIPPED-ON-LOAD-1: the browser rule now lives beside
// the load decision that uses it. Re-exported so its own guards still reach it.
export { resolveActiveSubAccount }

/**
 * ACTIVE-ACCOUNT-1 — record the active membership server side.
 *
 * One row per user, upserted on the user_id primary key. It stores the
 * MEMBERSHIP id rather than the business id, because a membership disappearing
 * takes the choice with it and a business id would linger after access was
 * removed.
 *
 * DEFENSIVE ABOUT A PRE-MIGRATION DATABASE, because the migration that creates
 * this table is print-only: 42P01 means it is not there yet, which is not a
 * problem the person switching businesses needs to hear about. Anything else
 * is logged, because a write that silently does not happen is how the two
 * sides drift apart again.
 */
async function persistActiveMembership(userId: string, teamMemberId: string): Promise<void> {
  // The table is not in the generated types until the migration is applied.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const { error } = await sb
    .from('user_active_membership')
    .upsert({ user_id: userId, team_member_id: teamMemberId }, { onConflict: 'user_id' })
  if (error && error.code !== '42P01' && error.code !== 'PGRST205') {
    console.warn('[AuthProvider] could not record the active business', error.message)
  }
}

/**
 * THE-ACTIVE-BUSINESS-IS-NOT-FLIPPED-ON-LOAD-1 — this person's own row, read with their
 * own client. "Not there yet" and "no row" are both none; any other error is failed,
 * which writes nothing, because a question that went unanswered is not an empty answer.
 */
async function readActiveMembership(userId: string): Promise<ServerChoice> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  try {
    const { data, error } = await sb
      .from('user_active_membership')
      .select('team_member_id, updated_at')
      .eq('user_id', userId)
      .maybeSingle()
    if (error) {
      return error.code === '42P01' || error.code === 'PGRST205'
        ? { state: 'none' }
        : { state: 'failed' }
    }
    const row = data as { team_member_id?: string; updated_at?: string } | null
    if (!row?.team_member_id) return { state: 'none' }
    const t = Date.parse(String(row.updated_at ?? ''))
    return {
      state: 'found',
      teamMemberId: row.team_member_id,
      updatedAt: Number.isFinite(t) ? t : 0,
    }
  } catch {
    return { state: 'failed' }
  }
}

async function fetchProfileById(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle()
  if (error && error.code !== 'PGRST116') {
    console.warn('Failed to fetch profile', error)
    return null
  }
  return (data as Profile | null) ?? null
}

async function fetchTeamMembersByOwnerId(ownerId: string): Promise<TeamMember[]> {
  // Phase 28C: returns every accepted team_members row for the team
  // this user belongs to. Used by Settings -> Team to render the
  // active members list. Distinct from fetchMyMembershipsByUserId
  // (which scopes by member_user_id, not team_owner_id).
  const { data, error } = await supabase
    .from('team_members')
    .select('*')
    .eq('team_owner_id', ownerId)
    .not('accepted_at', 'is', null)
    .order('created_at', { ascending: true })
  if (error) {
    console.warn('Failed to fetch team members', error)
    return []
  }
  return (data as TeamMember[] | null) ?? []
}

// Phase C-B1: was fetchTeamMemberByUserId (single row, .limit(1)).
// Now returns every accepted team_members row where this user is the
// member, one per sub_account they belong to. For users with a single
// sub_account this returns a singleton array.
async function fetchMyMembershipsByUserId(userId: string): Promise<TeamMember[]> {
  const { data, error } = await supabase
    .from('team_members')
    .select('*')
    .eq('member_user_id', userId)
    .not('accepted_at', 'is', null)
    .order('created_at', { ascending: true })
  if (error) {
    console.warn('Failed to fetch memberships', error)
    return []
  }
  return (data as TeamMember[] | null) ?? []
}

// Phase C-B1: hydrate every sub_account the user has access to in one
// shot. RLS allows a team member to SELECT any sub_account they have
// an accepted team_members row for (sub_accounts_member_select policy
// from Phase 28.6B). Returns is_default first so default-fallback
// selection logic can pick element 0.
async function fetchSubAccountsByIds(ids: string[]): Promise<SubAccount[]> {
  if (ids.length === 0) return []
  const { data, error } = await supabase
    .from('sub_accounts')
    .select('*')
    .in('id', ids)
    .order('is_default', { ascending: false })
  if (error) {
    console.warn('Failed to fetch sub_accounts', error)
    return []
  }
  // ACCOUNT-DELETABLE-1: a closed business is gone from every list, and the
  // switcher is a list. It selects *, so it reads the lifecycle columns the
  // moment they exist and treats every account as open until then.
  return visibleAccounts((data as SubAccount[] | null) ?? [])
}

// Phase 62.D: fetch the owner's merchant_group account_type.
// Returns 'solo' for users with no merchant_group (agents, very-
// new signups before the backfill trigger).
// NOTE: account_type column exists in Postgres but not in the
// auto-generated types until Tyler runs `npm run db:types`.
// The raw query bypasses typed select to avoid build errors.
async function fetchAccountType(userId: string): Promise<AccountType> {
  // account_type column exists in Postgres after Phase 62.D migration
  // but not yet in auto-generated types. Select '*' and cast.
  const { data, error } = await supabase
    .from('merchant_groups')
    .select('*')
    .eq('owner_user_id', userId)
    .limit(1)
    .maybeSingle()
  if (error || !data) return 'solo'
  const value = (data as unknown as Record<string, unknown>).account_type
  if (value === 'solo' || value === 'multi_business' || value === 'agency') return value
  return 'solo'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [profileExists, setProfileExists] = useState<boolean | null>(null)
  // Phase 62.D: account type from merchant_groups
  const [accountType, setAccountType] = useState<AccountType>('solo')
  // Phase 28B / C-B1: team membership state. teamLoaded mirrors the
  // tri-state shape (null = in flight, true = resolved). On resolve we
  // hydrate myMemberships (the user's own rows) AND availableSubAccounts
  // (the SubAccount rows pointed at by those memberships) together,
  // collapsing the prior two-effect chain into one fetch.
  const [myMemberships, setMyMemberships] = useState<TeamMember[]>([])
  const [availableSubAccounts, setAvailableSubAccounts] = useState<SubAccount[]>([])
  const [teamLoaded, setTeamLoaded] = useState<boolean | null>(null)
  // Phase 28C: separate lazy-fetched team roster (the OWNER's team).
  // Distinct shape and source from myMemberships above.
  const [teamMembers, setTeamMembers] = useState<TeamMember[] | null>(null)
  // Phase C-B1: active sub_account selection. Persisted to localStorage
  // scoped by user.id. Resolves on the same tick the team fetch
  // completes — the priority is stored value (if valid) -> default
  // sub_account -> first available.
  const [activeSubAccountId, setActiveSubAccountIdState] = useState<string | null>(null)

  // Phase 62.E.1 — track the prior auth user so we can detect
  // email_confirmed_at NULL→non-NULL transitions and emit the
  // email_verified activation event exactly once per verification.
  const prevUserRef = useRef<User | null>(null)

  useEffect(() => {
    let mounted = true

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return
      const nextUser = session?.user ?? null
      prevUserRef.current = nextUser
      setUser(nextUser)
      setAuthLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user ?? null
      // Phase 62.E.1 — detect email_confirmed_at NULL→non-NULL.
      const prevUser = prevUserRef.current
      const wasUnverified = !prevUser?.email_confirmed_at
      const isVerified = !!nextUser?.email_confirmed_at
      if (nextUser && wasUnverified && isVerified) {
        void emitActivationEvent({
          userId: nextUser.id,
          eventType: 'email_verified',
          metadata: {
            email: nextUser.email ?? null,
            confirmed_at: nextUser.email_confirmed_at,
          },
        })
      }
      prevUserRef.current = nextUser
      setUser(nextUser)
      setAuthLoading(false)
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const userId = user?.id ?? null

  // Phase R4.0: once-per-userId guard so applyAttributionIfPending
  // fires at most once per signed-in session even if the hydration
  // effect re-runs (StrictMode double-mount, fast nav). Reset when
  // userId flips so a sign-out + sign-in by a different user
  // re-evaluates.
  const attributionAttemptedForUserRef = useRef<string | null>(null)

  useEffect(() => {
    if (!userId) {
      reportUser(null)
      posthog.reset()
      setProfile(null)
      setProfileExists(null)
      setMyMemberships([])
      setAvailableSubAccounts([])
      setActiveSubAccountIdState(null)
      setTeamLoaded(null)
      setTeamMembers(null)
      setAccountType('solo')
      attributionAttemptedForUserRef.current = null
      return
    }
    reportUser({ id: userId, email: user?.email })
    posthog.identify(userId, { email: user?.email })
    let cancelled = false
    // Do NOT reset profile / team here. Keep stale data visible until
    // the new fetches complete. This prevents loading=true windows
    // that cause downstream components to render null and flash blank
    // during normal nav.
    //
    // Phase L2: defensive .catch. Without it, a thrown Supabase client
    // error (network failure, CORS, etc.) bypasses the .then and leaves
    // teamLoaded at null forever, stranding `loading=true` for the
    // entire session. We resolve to null/null on throw so the loading
    // gate eventually opens and consumers can react accordingly.
    //
    // Phase C-B1: combined fetch. Profile + memberships + the
    // SubAccount rows for those memberships all resolve in one chain.
    // Active selection is set inline so consumers see a consistent
    // state on the first render after teamLoaded flips true.
    void Promise.all([
      fetchProfileById(userId),
      fetchMyMembershipsByUserId(userId),
      fetchAccountType(userId),
      readActiveMembership(userId),
    ])
      .then(async ([p, memberships, acctType, server]) => {
        if (cancelled) return
        setProfile(p)
        setProfileExists(p !== null)
        setMyMemberships(memberships)
        setAccountType(acctType)

        const subAccountIds = memberships
          .map((m) => m.sub_account_id)
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
        const subs = await fetchSubAccountsByIds(subAccountIds)
        if (cancelled) return
        setAvailableSubAccounts(subs)

        // THE-ACTIVE-BUSINESS-IS-NOT-FLIPPED-ON-LOAD-1: the server's row wins and
        // this browser is set to match it. The browser's memory reaches the server
        // only through setActiveSubAccount, an explicit switch.
        let storedId: string | null = null
        let storedAt = 0
        try {
          storedId = localStorage.getItem(activeSubAccountStorageKey(userId))
          storedAt = readChoiceStamp(localStorage.getItem(activeSubAccountStampKey(userId)))
        } catch {
          storedId = null
        }
        const onLoad = activeOnLoad({ server, storedId, storedAt, memberships, subs })
        setActiveSubAccountIdState(onLoad.activeId)
        try {
          if (onLoad.forget) {
            localStorage.removeItem(activeSubAccountStorageKey(userId))
            localStorage.removeItem(activeSubAccountStampKey(userId))
          }
          if (onLoad.remember) {
            localStorage.setItem(activeSubAccountStorageKey(userId), onLoad.remember)
            localStorage.setItem(activeSubAccountStampKey(userId), String(Date.now()))
          }
        } catch {
          // Private mode: the server row still decides.
        }
        if (onLoad.write) void persistActiveMembership(userId, onLoad.write)

        setTeamLoaded(true)

        // Phase R4.0: try to apply a captured ?ref= attribution. Fire
        // only on first signup (exactly one sub_account that hasn't
        // been attributed yet) — Multi-Account users with multiple
        // sub_accounts attribute via CreateSubAccountModal explicitly.
        // Once-per-session guard via the ref above; the RPC itself is
        // also idempotent (already_attributed → silent no-op).
        if (
          attributionAttemptedForUserRef.current !== userId &&
          memberships.length === 1 &&
          subs.length === 1 &&
          subs[0].attribution_timestamp === null
        ) {
          attributionAttemptedForUserRef.current = userId
          void applyAttributionIfPending(supabase, subs[0].id)
        }
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[AuthProvider] profile/team fetch threw', err)
        setProfile(null)
        setProfileExists(false)
        setMyMemberships([])
        setAvailableSubAccounts([])
        setActiveSubAccountIdState(null)
        setAccountType('solo')
        setTeamLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  // Phase C-B1: derive currentTeamMember + currentSubAccount from the
  // active selection. Cheap memo since both arrays are small (one
  // entry per sub_account the user belongs to).
  const currentTeamMember = useMemo<TeamMember | null>(() => {
    if (!activeSubAccountId) return null
    return myMemberships.find((m) => m.sub_account_id === activeSubAccountId) ?? null
  }, [activeSubAccountId, myMemberships])

  const currentSubAccount = useMemo<SubAccount | null>(() => {
    if (!activeSubAccountId) return null
    return availableSubAccounts.find((s) => s.id === activeSubAccountId) ?? null
  }, [activeSubAccountId, availableSubAccounts])

  // ACTIVE-ACCOUNT-1 — switching writes the choice to the SERVER as well as to
  // this browser.
  //
  // WHAT THIS FIXES. Before this wave the switcher stored the selection in
  // localStorage and nowhere else, so the server had no way to know which
  // business a person was looking at. That is not a disagreement the two sides
  // could resolve - the assistant simply could not see the choice, and after
  // READ-DOOR-1 a person with two businesses got a refusal from the assistant
  // while their screen showed one of them perfectly well. The interface and
  // the door now read the same fact.
  //
  // The row references the MEMBERSHIP, not the business, so a person who loses
  // access to a business loses their active choice in the same statement. The
  // write is scoped by RLS to the caller's own row and to a membership that is
  // theirs and accepted, so this client-side validity check is convenience and
  // the database is the boundary.
  //
  // localStorage stays, and is written FIRST. It is what makes the choice
  // survive a reload before the network answers, and what keeps the switcher
  // working offline and in private mode. The server write is the one that lets
  // Ghost see the same thing; it is deliberately not awaited, because a person
  // switching businesses should not wait on a round trip to see their own
  // screen change.
  const setActiveSubAccount = useCallback(
    (subAccountId: string) => {
      if (!userId) return
      const membership = myMemberships.find((m) => m.sub_account_id === subAccountId)
      if (!membership) {
        console.warn(
          '[AuthProvider] setActiveSubAccount: sub_account_id not in user membership list',
          { subAccountId },
        )
        return
      }
      setActiveSubAccountIdState(subAccountId)
      try {
        localStorage.setItem(activeSubAccountStorageKey(userId), subAccountId)
        // The stamp travels with the choice, so a later account can outrank it.
        localStorage.setItem(activeSubAccountStampKey(userId), String(Date.now()))
      } catch {
        // localStorage may throw in private mode; selection still
        // applies for this session.
      }
      void persistActiveMembership(userId, membership.id)
    },
    [userId, myMemberships],
  )

  const refreshProfile = useCallback(async () => {
    if (!userId) {
      setProfile(null)
      setProfileExists(null)
      return
    }
    const p = await fetchProfileById(userId)
    setProfile(p)
    setProfileExists(p !== null)
  }, [userId])

  // Phase C-B1: refreshTeam now refetches the array of memberships
  // and the corresponding sub_accounts together. Active selection is
  // preserved if still valid; otherwise falls back to default ->
  // first.
  const refreshTeam = useCallback(async () => {
    if (!userId) {
      setMyMemberships([])
      setAvailableSubAccounts([])
      setActiveSubAccountIdState(null)
      setTeamLoaded(null)
      return
    }
    const memberships = await fetchMyMembershipsByUserId(userId)
    setMyMemberships(memberships)
    const subAccountIds = memberships
      .map((m) => m.sub_account_id)
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
    const subs = await fetchSubAccountsByIds(subAccountIds)
    setAvailableSubAccounts(subs)
    setActiveSubAccountIdState((cur) => {
      if (cur && memberships.some((m) => m.sub_account_id === cur)) {
        return cur
      }
      return subs.find((s) => s.is_default)?.id ?? subs[0]?.id ?? null
    })
    setTeamLoaded(true)
  }, [userId])

  // Phase 28C: lazy-fetch the full team_members list for Settings ->
  // Team. Keyed off the active membership's team_owner_id so a switch
  // between sub_accounts (different teams) refetches the right roster.
  const ownerIdForList = currentTeamMember?.team_owner_id ?? null
  useEffect(() => {
    if (!ownerIdForList) {
      setTeamMembers(null)
      return
    }
    let cancelled = false
    void fetchTeamMembersByOwnerId(ownerIdForList).then((rows) => {
      if (cancelled) return
      setTeamMembers(rows)
    })
    return () => {
      cancelled = true
    }
  }, [ownerIdForList])

  const refreshTeamMembers = useCallback(async () => {
    if (!ownerIdForList) {
      setTeamMembers(null)
      return
    }
    const rows = await fetchTeamMembersByOwnerId(ownerIdForList)
    setTeamMembers(rows)
  }, [ownerIdForList])

  // Phase C-B1: refreshSubAccount re-pulls the SubAccount row for the
  // active selection only (cheap; one row). Used by callers that just
  // updated the row and want their local copy refreshed without a
  // full team refetch.
  const refreshSubAccount = useCallback(async () => {
    if (!activeSubAccountId) return
    const { data, error } = await supabase
      .from('sub_accounts')
      .select('*')
      .eq('id', activeSubAccountId)
      .maybeSingle()
    if (error) {
      console.warn('[AuthProvider] refreshSubAccount failed', error)
      return
    }
    const fresh = (data as SubAccount | null) ?? null
    if (!fresh) return
    setAvailableSubAccounts((cur) => cur.map((s) => (s.id === fresh.id ? fresh : s)))
  }, [activeSubAccountId])

  const signOut = useCallback(async () => {
    await signOutEverywhere()
  }, [])

  // PUSH-1a: the token-rotation watch. NOT an ask — startPushTokenWatch never
  // shows a dialog and does nothing at all unless permission is already
  // granted. It exists because APNs hands out a NEW token at its own
  // discretion (a restore, a reinstall of the OS, its own schedule), and a
  // rotation that arrives while the app is running is only caught by a
  // listener that was already installed. Lives here because this is where the
  // member identity the token is keyed to is derived.
  const watchMemberId = currentTeamMember?.id ?? null
  const watchSubAccountId = currentSubAccount?.id ?? null
  useEffect(() => {
    if (!watchMemberId || !watchSubAccountId) return
    void startPushTokenWatch({
      teamMemberId: watchMemberId,
      subAccountId: watchSubAccountId,
    })
  }, [watchMemberId, watchSubAccountId])

  // Phase C-B1: subAccountLoaded is no longer a separate flag because
  // sub_accounts hydrate in the same effect as memberships. teamLoaded
  // covers both. The combined loading gate is unchanged in semantics:
  // wait for auth + profile + team (which now includes sub_accounts).
  const loading = authLoading || (user !== null && (profileExists === null || teamLoaded === null))

  // Phase L2 / C-B1: dev-mode diagnostic. Logs every state transition
  // so future "currentSubAccountId is null" debugging can pinpoint
  // exactly which fetch returned null. Stripped from production by
  // import.meta.env.DEV.
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[AuthProvider] state', {
        userId: user?.id ?? null,
        authLoading,
        profileExists,
        teamLoaded,
        myMembershipsCount: myMemberships.length,
        availableSubAccountsCount: availableSubAccounts.length,
        activeSubAccountId,
        currentTeamMemberId: currentTeamMember?.id ?? null,
        currentSubAccountId: currentSubAccount?.id ?? null,
        teamMembersCount: teamMembers?.length ?? null,
        accountType,
        loading,
      })
    }
  }, [
    user,
    authLoading,
    profileExists,
    teamLoaded,
    myMemberships,
    availableSubAccounts,
    activeSubAccountId,
    currentTeamMember,
    currentSubAccount,
    teamMembers,
    accountType,
    loading,
  ])

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        profileExists,
        currentTeamMember,
        currentTeamRole: currentTeamMember?.role ?? null,
        currentTeamOwnerId: currentTeamMember?.team_owner_id ?? null,
        teamMembers,
        currentSubAccount,
        currentSubAccountId: activeSubAccountId,
        availableSubAccounts,
        setActiveSubAccount,
        accountType,
        loading,
        signOut,
        refreshProfile,
        refreshTeam,
        refreshTeamMembers,
        refreshSubAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
